const ABSOLUTE_URL_REGEX = /^(?:[a-z][a-z0-9+.-]*:|\/\/)/i;

const stripBasePath = (path: string, base: string) => {
  //
  // Logic adapted from react-router
  // https://github.com/remix-run/react-router/blob/main/packages/router/utils.ts
  //
  if (base === "/") return path;

  if (!path.toLowerCase().startsWith(base.toLowerCase())) {
    return null;
  }

  const startIndex = base.endsWith("/") ? base.length - 1 : base.length;
  const nextChar = path.charAt(startIndex);
  if (nextChar != null && nextChar !== "/") {
    return null;
  }
  return path.slice(startIndex) ?? "/";
};

const tryParseUrl = (href: string | undefined) => {
  if (href === undefined) return undefined;
  try {
    return new URL(href);
  } catch {
    return undefined;
  }
};

export const normalizeHref = (
  href: string,
  applicationBaseHref?: string,
  permalinkBaseHref?: string,
) => {
  //
  // Logic adapted from react-router
  // https://github.com/remix-run/react-router/blob/main/packages/react-router-dom/index.tsx
  //

  const applicationBase = tryParseUrl(applicationBaseHref);
  const permalinkBase = tryParseUrl(permalinkBaseHref);

  const result = {
    isExternal: false,
    isRelative: false,
    isPermalink: false,
    normalized: href,
  };

  if (!ABSOLUTE_URL_REGEX.test(href)) {
    result.isRelative = true;
    return result;
  }

  let url: URL;
  try {
    url = new URL(href);
  } catch {
    return result;
  }

  result.normalized = url.toString();

  if (url.protocol === "data:") return result;

  result.isExternal = true;

  for (const potentialBase of [applicationBase, permalinkBase]) {
    if (potentialBase == null) continue;

    if (
      url.protocol !== potentialBase.protocol ||
      url.host.toLowerCase() !== potentialBase.host.toLowerCase()
    ) {
      continue;
    }

    const stripped = stripBasePath(url.pathname, potentialBase.pathname);

    //
    // Domain and protocol match, but we are on a different path than the
    // base specifies
    //
    if (stripped == null) continue;

    result.isExternal = false;
    if (potentialBase === applicationBase) {
      result.isRelative = true;
      result.normalized = stripped + url.search + url.hash;
    }
    if (potentialBase === permalinkBase) {
      result.isPermalink = true;
    }
  }

  return result;
};
